/**
 * Regeln rund um das Wirken von Skills (Masterprompt Abschnitte 28, 29, 46, 52).
 *
 * Auch hier gilt: Der Server entscheidet. Der Client darf einen Skill anfragen,
 * aber ob er zustande kommt, ob genug Mana da ist und wen er trifft, bestimmt
 * ausschliesslich der Server.
 */

import {
  HERO_SKILL_LOCK_LUFT_SECONDS,
  MARKED_PREY,
  SKILL_TREE_TUNING,
  MAX_TOTAL_DAMAGE_REDUCTION,
  STAND_YOUR_GROUND,
  type SkillDefinition,
} from '@dueline/game-data';
import { clamp, distance, type Vec2 } from '@dueline/shared';
import {
  clampToRange,
  directionTo,
  isInsideCircle,
  isInsideCone,
  isNearSegment,
} from './geometry.js';

/** Warum ein Skill nicht gewirkt werden konnte. */
export type SkillRejection = 'unknown_skill' | 'hero_dead' | 'on_cooldown' | 'not_enough_mana';

export interface SkillCastCheck {
  allowed: boolean;
  reason: SkillRejection | null;
}

/** Zustand des Heroes, soweit die Pruefung ihn braucht. */
export interface CasterState {
  alive: boolean;
  mana: number;
  /** Verbleibende Abklingzeit dieses Skills in Sekunden. */
  cooldownRemaining: number;
}

/**
 * Darf dieser Skill jetzt gewirkt werden?
 *
 * Diese Pruefung laeuft IMMER auf dem Server. Der Client kann dieselbe Funktion
 * benutzen, um Schaltflaechen auszugrauen - das ist aber nur Anzeige.
 */
export function canCastSkill(
  caster: CasterState,
  definition: SkillDefinition | undefined,
): SkillCastCheck {
  if (!definition) return { allowed: false, reason: 'unknown_skill' };
  if (!caster.alive) return { allowed: false, reason: 'hero_dead' };
  if (caster.cooldownRemaining > 0) return { allowed: false, reason: 'on_cooldown' };
  if (caster.mana < definition.manaCost) return { allowed: false, reason: 'not_enough_mana' };
  return { allowed: true, reason: null };
}

/**
 * Bringt den vom Client gemeldeten Zielpunkt in einen gueltigen Bereich.
 *
 * Ein manipulierter Client koennte irgendeinen Punkt schicken. Hier wird er auf
 * die Reichweite des Skills gekuerzt - und ungueltige Zahlen werden durch die
 * Blickrichtung des Heroes ersetzt.
 */
export function resolveAimPoint(
  origin: Vec2,
  aim: Vec2 | null,
  facing: number,
  definition: SkillDefinition,
): Vec2 {
  const reichweite = Math.max(definition.castRange, definition.dashDistance);

  if (!aim || !Number.isFinite(aim.x) || !Number.isFinite(aim.y)) {
    // Ohne brauchbare Eingabe wird in Blickrichtung gewirkt.
    return {
      x: origin.x + Math.cos(facing) * reichweite,
      y: origin.y + Math.sin(facing) * reichweite,
    };
  }

  return clampToRange(origin, aim, reichweite);
}

/** Etwas, das von einem Skill getroffen werden kann. */
export interface SkillTargetCandidate {
  id: string;
  position: Vec2;
  hp: number;
}

/**
 * Ermittelt alle Gegner, die ein Skill trifft.
 *
 * @param origin Position des Heroes beim Wirken.
 * @param aim    Bereits gepruefter Zielpunkt (siehe resolveAimPoint).
 */
export function findSkillTargets(
  origin: Vec2,
  aim: Vec2,
  definition: SkillDefinition,
  candidates: readonly SkillTargetCandidate[],
): SkillTargetCandidate[] {
  const lebende = candidates.filter((candidate) => candidate.hp > 0);

  switch (definition.targeting) {
    case 'self':
      return [];

    case 'cone': {
      const richtung = directionTo(origin, aim);
      return lebende.filter((candidate) =>
        isInsideCone(
          origin,
          richtung,
          definition.castRange,
          definition.coneAngleDegrees,
          candidate.position,
        ),
      );
    }

    case 'skillshot': {
      // Getroffen wird alles entlang der Bahn - bei einem Dash also alles,
      // durch das der Hero hindurchstuerzt.
      return lebende.filter((candidate) =>
        isNearSegment(origin, aim, definition.radius, candidate.position),
      );
    }

    case 'ground_aoe':
      return lebende.filter((candidate) =>
        isInsideCircle(aim, definition.radius, candidate.position),
      );
  }
}

/**
 * Fuellung der Passive "Stand Your Ground", 0 bis 1
 * (Masterprompt Abschnitt 57).
 *
 * Gezaehlt werden die lebenden Gegner im Umkreis des Heroes. Ab acht Gegnern
 * ist die Wirkung voll - jeder weitere bringt nichts mehr. Das ist Absicht:
 * Der Vanguard soll in der Welle stark sein, nicht unbesiegbar.
 *
 * Aus DIESER einen Zahl kommen beide Wirkungen der Passive: die
 * Schadensreduktion und der Income-Zuschlag (v0.48). So gibt es nur eine
 * Zaehlung, und das Talent "Wall" wirkt automatisch auf beide.
 */
export function getStandYourGroundFill(
  heroPosition: Vec2,
  enemies: readonly SkillTargetCandidate[],
  /**
   * Ab wie vielen Gegnern die Passive voll wirkt.
   *
   * Normal sind acht. Das Talent "Wall" (Level 8) senkt den Wert - deshalb ist
   * er ueberschreibbar und steht nicht fest im Code.
   */
  enemiesForMaximum: number = STAND_YOUR_GROUND.enemiesForMaximum,
): number {
  let inReichweite = 0;
  for (const enemy of enemies) {
    if (enemy.hp <= 0) continue;
    if (distance(heroPosition, enemy.position) <= STAND_YOUR_GROUND.radius) {
      inReichweite += 1;
    }
  }

  const schwelle = Math.max(1, enemiesForMaximum);
  return Math.min(inReichweite, schwelle) / schwelle;
}

/** Schadensreduktion durch "Stand Your Ground". */
export function getStandYourGroundReduction(
  heroPosition: Vec2,
  enemies: readonly SkillTargetCandidate[],
  enemiesForMaximum: number = STAND_YOUR_GROUND.enemiesForMaximum,
): number {
  return (
    getStandYourGroundFill(heroPosition, enemies, enemiesForMaximum) *
    STAND_YOUR_GROUND.maximumDamageReduction
  );
}

/**
 * Income-Zuschlag durch "Stand Your Ground" (v0.48).
 *
 * Er wird nicht in dem Augenblick abgerechnet, in dem das Income faellig ist,
 * sondern als Durchschnitt ueber das Auszahlungsfenster - siehe
 * STAND_YOUR_GROUND.maximumIncomeRatio.
 */
export function getStandYourGroundIncomeRatio(
  heroPosition: Vec2,
  enemies: readonly SkillTargetCandidate[],
  enemiesForMaximum: number = STAND_YOUR_GROUND.enemiesForMaximum,
): number {
  return (
    getStandYourGroundFill(heroPosition, enemies, enemiesForMaximum) *
    STAND_YOUR_GROUND.maximumIncomeRatio
  );
}

/**
 * Fasst mehrere Schadensreduktionen zusammen.
 *
 * Sie werden MULTIPLIKATIV verrechnet, nicht addiert: Zwei Mal 50 Prozent
 * ergeben 75 Prozent, nicht 100. Dadurch kann Schaden nie vollstaendig
 * verschwinden - und genau das ist gewollt, denn Unverwundbarkeit ist
 * ausdruecklich nicht vorgesehen (Masterprompt Abschnitt 57).
 *
 * Zusaetzlich greift eine harte Obergrenze.
 */
export function combineDamageReductions(...reductions: readonly number[]): number {
  let verbleibend = 1;
  for (const reduction of reductions) {
    const sicher = clamp(Number.isFinite(reduction) ? reduction : 0, 0, 1);
    verbleibend *= 1 - sicher;
  }
  return clamp(1 - verbleibend, 0, MAX_TOTAL_DAMAGE_REDUCTION);
}

/** Wendet eine Schadensreduktion auf einen Schadenswert an. */
export function applyDamageReduction(damage: number, reduction: number): number {
  const sicher = clamp(Number.isFinite(reduction) ? reduction : 0, 0, 1);
  return Math.max(0, damage) * (1 - sicher);
}

/** Regeneriert die Ressource des Heroes, begrenzt auf das Maximum. */
export function regenerateResource(
  current: number,
  maximum: number,
  perSecond: number,
  dt: number,
): number {
  if (current >= maximum) return maximum;
  return Math.min(maximum, current + Math.max(0, perSecond) * Math.max(0, dt));
}

/**
 * Reihenfolge der Getroffenen auf einer Bahn: vorne zuerst.
 *
 * Gebraucht fuer den Piercing Shot des Deadeye - der Erste bekommt den vollen
 * Schaden, die dahinter weniger. Ohne feste Reihenfolge haenge das davon ab,
 * in welcher Reihenfolge die Creeps zufaellig im Zustand stehen.
 */
export function sortByDistanceFrom(
  origin: Vec2,
  targets: readonly SkillTargetCandidate[],
): SkillTargetCandidate[] {
  return [...targets].sort((a, b) => distance(origin, a.position) - distance(origin, b.position));
}

/**
 * Schadensanteil des n-ten Getroffenen auf einer Bahn (Piercing Shot).
 *
 * @param index 0 fuer den Vordersten.
 */
export function getPierceDamageFactor(
  index: number,
  falloff: { perTarget: number; minimum: number } | undefined,
): number {
  if (!falloff) return 1;
  return Math.max(falloff.minimum, 1 - falloff.perTarget * index);
}

/**
 * Passive "Marked Prey" des Deadeye: Wie viele Stufen hat das neue Ziel?
 *
 * Dasselbe Ziel wie zuletzt: eine Stufe mehr, bis zum Maximum. Ein anderes
 * Ziel: wieder von vorn. So kann man sich die Stufen nicht an einer Welle
 * aufbauen und dann auf den Brute umschwenken.
 */
export function nextMarkedStacks(
  previousTargetId: string | null,
  targetId: string,
  stacks: number,
  maxStacks: number = MARKED_PREY.maxStacks,
): number {
  if (previousTargetId !== targetId) return 1;
  return Math.min(maxStacks, stacks + 1);
}

/**
 * Schadensfaktor aus den aufgestauten Stufen.
 *
 * Die erste Stufe gibt noch nichts: Der erste Schuss auf ein neues Ziel ist
 * ein ganz normaler Schuss.
 */
export function getMarkedPreyFactor(
  stacks: number,
  damagePerStack: number = MARKED_PREY.damagePerStack,
): number {
  return 1 + Math.max(0, stacks - 1) * damagePerStack;
}

/**
 * Zuschlag gegen angeschlagene Ziele (Kill Shot des Deadeye).
 *
 * Bei vollem Leben gibt es nichts, kurz vor dem Tod den vollen Zuschlag.
 */
export function getExecuteFactor(
  hp: number,
  maxHp: number,
  executeBonus: number | undefined,
): number {
  if (!executeBonus || maxHp <= 0) return 1;
  const fehlend = clamp(1 - hp / maxHp, 0, 1);
  return 1 + executeBonus * fehlend;
}

/**
 * Der erste Gegner auf einer Bahn (jeder Schuss des Barrage).
 *
 * Anders als beim Piercing Shot bleibt es bei EINEM Getroffenen - dafuer faellt
 * der Schuss oefter.
 */
export function findFirstTargetOnLine(
  origin: Vec2,
  aim: Vec2,
  halfWidth: number,
  candidates: readonly SkillTargetCandidate[],
): SkillTargetCandidate | null {
  const treffer = candidates.filter(
    (candidate) => candidate.hp > 0 && isNearSegment(origin, aim, halfWidth, candidate.position),
  );
  return sortByDistanceFrom(origin, treffer)[0] ?? null;
}

/**
 * Faktor auf alle Reichweiten eines Heroes (Talent "Eagle Eye" des Deadeye).
 *
 * Steht hier und nicht im Server, weil BEIDE Seiten damit rechnen muessen: Der
 * Server kuerzt den Zielpunkt darauf, die Zielvorschau im Client zeigt genau
 * denselben Ring. Zwei getrennte Rechnungen waeren frueher oder spaeter zwei
 * verschiedene Reichweiten.
 */
export function getHeroRangeFactor(talentId: string): number {
  return talentId === 'eagle_eye' ? SKILL_TREE_TUNING.talentEagleEye.rangeFactor : 1;
}

/** Hoechste Stufe von Marked Prey - das Talent "Tracker" hebt sie an. */
export function getMaxMarkedStacks(talentId: string): number {
  return talentId === 'hunter' ? SKILL_TREE_TUNING.talentHunter.maxStacks : MARKED_PREY.maxStacks;
}

/**
 * Der Skill, wie er fuer DIESEN Hero gilt - mit seiner Reichweite.
 *
 * Server und Client benutzen dieselbe Funktion: Der Server kuerzt den
 * Zielpunkt darauf, der Client zeichnet denselben Ring.
 */
export function withHeroRange(definition: SkillDefinition, talentId: string): SkillDefinition {
  const faktor = getHeroRangeFactor(talentId);
  return faktor === 1 ? definition : { ...definition, castRange: definition.castRange * faktor };
}

/**
 * Wie lange das Wirken dieser Faehigkeit den Hero bindet, in Sekunden
 * (Produktentscheidung vom 25.10.2026).
 *
 * Bis zum Treffmoment der Bewegung plus etwas Luft. Der Treffmoment ist
 * reine Optik - der Schaden faellt auf dem Server sofort -, aber er ist der
 * Augenblick, in dem die Figur ihren Schlag, Schuss oder Wurf AUSFUEHRT.
 * Wer vorher losliefe, rutschte durch seinen eigenen Zauber.
 *
 * Ein Kanal bindet laenger: Die Barrage des Deadeye schiesst ueber mehrere
 * Sekunden, und so lange steht er. Gebunden ist immer die groessere der
 * beiden Zeiten.
 */
export function skillBindung(
  skill: { hitSeconds: number; channel?: { seconds: number } },
  luft: number = HERO_SKILL_LOCK_LUFT_SECONDS,
): number {
  const treffer = Number.isFinite(skill.hitSeconds) ? Math.max(0, skill.hitSeconds) : 0;
  const bindung = treffer + Math.max(0, luft);
  return skill.channel ? Math.max(bindung, skill.channel.seconds) : bindung;
}
